import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { SeoResponse, SiteLanguage } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { Card, HEADLINE, KICKER, TabBar, UNDER } from "./chrome.js";

/**
 * Where the articles go: the publishing half of the customer's website
 * ([21 §7](../../../../docs/21-articles-and-seo.md#7--connectors), ADR 0033 §5).
 *
 * **The address is not asked for here**
 * ([ADR 0040](../../../../docs/adr/0040-a-business-has-one-website-address-and-it-is-typed-on-the-business-page.md)).
 * A business has one website and it is typed on the Business page, under Website & analytics, with everywhere else that
 * business is — the same rule the mailbox, the channels and the Google listing already follow, and the same
 * sentence Analytics says when nothing is counted yet. This screen had its own field and its own stored copy, so
 * a business could disagree with itself about where its own website was; it shows the address instead.
 *
 * What is left is written for two people at once — the owner, who says where articles hang off that address and
 * in which languages, and the developer who will make the site read the feed, whose half is the feed address, a
 * key shown once, and the three steps of a Cloudflare Pages setup written out rather than linked to.
 *
 * The promise this screen makes and must keep: Joogoos holds no key to your code, your repository or your host.
 */

const BLANK: SiteLanguage = { lang: "en", prefix: "", default: true };

export function ConnectSite({ address, suggested, onDone, onBusiness }: {
  address: string | null; onDone: () => void; onBusiness?: (() => void) | undefined;
  /** What the homepage already links to, so the offer does not open on `/` (R-1). */
  suggested?: SeoResponse["suggested"] | undefined;
}) {
  return (
    <div className="h-full flex flex-col min-h-0">
      <TabBar title="SEO" note="Articles on your own website" />
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-6">
        <div className="max-w-[640px]">
          <p className={`${KICKER} text-coral`}>Nothing is connected yet</p>
          <h2 className={HEADLINE}>Articles published on your own domain.</h2>
          <p className={UNDER}>
            Joogoos writes blog articles from what your business knows, you approve every one, and they are published
            on <b>your own domain</b> — so the search credit is yours, not ours.
          </p>
          {/* The three promises, made where the address is typed rather than in a page nobody opens twice. */}
          <ul className="mt-4 flex flex-col gap-1.5">
            {[
              "Nothing is written until you accept a topic.",
              "Nothing is published until you press Approve.",
              "Joogoos never gets a key to your code, your repository or your hosting.",
            ].map((line) => (
              <li key={line} className="flex gap-2 text-[12.5px] text-ink-2 leading-relaxed">
                <Icon name="check" size={14} className="shrink-0 mt-0.5 text-good" />
                {line}
              </li>
            ))}
          </ul>
          {address ? (
            <div className="mt-5 rounded-xl border border-line bg-panel p-4 flex flex-col gap-3.5">
              <Address address={address} onBusiness={onBusiness} />
              <Form onDone={onDone} suggested={suggested} />
            </div>
          ) : <NoAddress onBusiness={onBusiness} />}
        </div>
      </div>
    </div>
  );
}

/** No address, so nowhere to publish. One sentence and one button, and not a second form. */
function NoAddress({ onBusiness }: { onBusiness?: (() => void) | undefined }) {
  return (
    <div className="mt-5 rounded-xl border border-line bg-panel p-4">
      <p className="text-[13.5px] font-semibold">Joogoos needs your website address first.</p>
      <p className="text-[12.5px] text-muted leading-relaxed mt-1">
        It is given once, on your Business page under Website & analytics — the same address Joogoos counts visits
        to. Articles are published under it.
      </p>
      {onBusiness ? (
        <button onClick={onBusiness} className="mt-3 bg-ink text-white rounded-lg px-3.5 py-2 text-[13px] font-semibold">
          Set it up on the Business page
        </button>
      ) : null}
    </div>
  );
}

/** The address, as a fact rather than a field: this screen shows it and the Business page changes it (ADR 0040). */
function Address({ address, onBusiness }: { address: string; onBusiness?: (() => void) | undefined }) {
  return (
    <div className="rounded-lg bg-page border border-line px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className={`${KICKER} text-muted`}>Published on</span>
      <span className="text-[13.5px] font-semibold">{address.replace(/^https?:\/\//, "")}</span>
      {onBusiness ? (
        <button onClick={onBusiness} className="ml-auto text-[12px] font-semibold text-coral hover:text-coral-2">
          Change it on the Business page
        </button>
      ) : (
        <span className="ml-auto text-[11.5px] text-muted">changed on the Business page</span>
      )}
    </div>
  );
}

function Form({ onDone, existing, suggested }: {
  onDone: () => void; existing?: SeoResponse["site"]; suggested?: SeoResponse["suggested"] | undefined;
}) {
  const [blogPath, setBlogPath] = useState(existing?.blogPath ?? "/blog");
  const [trailingSlash, setTrailingSlash] = useState(existing?.trailingSlash ?? false);
  const [languages, setLanguages] = useState<SiteLanguage[]>(existing?.languages.length ? existing.languages : [BLANK]);
  const [offers, setOffers] = useState<Record<string, { path: string; label: string }>>(
    existing?.offers ?? { en: suggested?.offer ?? { path: "/", label: "Get in touch" } });

  const m = useMutation({
    mutationFn: () => api.connectArticleSite({ kind: "static_feed", blogPath, trailingSlash, languages, offers }),
    onSuccess: onDone,
  });

  const setLang = (i: number, patch: Partial<SiteLanguage>) =>
    setLanguages(languages.map((l, n) => (n === i ? { ...l, ...patch } : n === 0 && patch.default ? { ...l, default: false } : l)));

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => { e.preventDefault(); m.mutate(); }}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Blog path" hint="Where they hang off.">
          <input value={blogPath} onChange={(e) => setBlogPath(e.target.value)} placeholder="/blog" required
            className="w-full bg-page border border-line rounded-lg px-3 py-2 text-[13.5px]" />
        </Field>
        <Field label="Addresses end in a slash" hint="Match what your site already does.">
          <label className="flex items-center gap-2 text-[13px] pt-1.5">
            <input type="checkbox" checked={trailingSlash} onChange={(e) => setTrailingSlash(e.target.checked)} />
            /blog/an-article{trailingSlash ? "/" : ""}
          </label>
        </Field>
      </div>

      <Field label="Languages" hint="The first is the one articles are written in; the others are translated from it.">
        <div className="flex flex-col gap-1.5">
          {/* Four boxes in a row are four questions, and an unlabelled box is a guess. On a phone the row wraps
              and the headings would wrap with it into nonsense, so there they come off and the placeholders do
              the naming. */}
          <div className="hidden md:flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[.05em] text-faint">
            <span className="w-16">Language</span>
            <span className="w-28">Path prefix</span>
            <span className="w-28">Offer page</span>
            <span className="flex-1">The words that link to it</span>
          </div>
          {languages.map((l, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input value={l.lang} onChange={(e) => setLang(i, { lang: e.target.value.toLowerCase().slice(0, 2) })}
                aria-label={`Language ${i + 1}`} placeholder="fr" className="w-16 bg-page border border-line rounded-lg px-2 py-1.5 text-[13px]" />
              <input value={i === 0 ? "" : l.prefix} onChange={(e) => setLang(i, { prefix: e.target.value })} disabled={i === 0}
                aria-label={`Path prefix ${i + 1}`} placeholder={i === 0 ? "(no prefix)" : "/fr"}
                className="w-28 bg-page border border-line rounded-lg px-2 py-1.5 text-[13px] disabled:text-faint" />
              <input value={offers[l.lang]?.path ?? "/"} onChange={(e) => setOffers({ ...offers, [l.lang]: { path: e.target.value, label: offers[l.lang]?.label ?? "Get in touch" } })}
                aria-label={`Offer page ${i + 1}`} placeholder="/audit" className="w-28 bg-page border border-line rounded-lg px-2 py-1.5 text-[13px]" />
              <input value={offers[l.lang]?.label ?? ""} onChange={(e) => setOffers({ ...offers, [l.lang]: { path: offers[l.lang]?.path ?? "/", label: e.target.value } })}
                aria-label={`Offer link words ${i + 1}`} placeholder="Start your free audit" className="flex-1 min-w-[160px] bg-page border border-line rounded-lg px-2 py-1.5 text-[13px]" />
              {languages.length > 1 && i > 0 ? (
                <button type="button" onClick={() => setLanguages(languages.filter((_, n) => n !== i))} aria-label={`Remove language ${i + 1}`} className="text-muted hover:text-coral">
                  <Icon name="x" size={14} />
                </button>
              ) : null}
            </div>
          ))}
          <button type="button" onClick={() => setLanguages([...languages, { lang: "", prefix: "", default: false }])}
            className="self-start flex items-center gap-1 text-[12px] font-semibold text-coral hover:text-coral-2">
            <Icon name="plus" size={13} /> Another language
          </button>
        </div>
      </Field>

      <p className="text-[11.5px] text-muted leading-relaxed">
        The words beside each offer page are the ones every article's last link will read. They are yours, not the
        model's: code puts that link in.
      </p>

      {m.error ? <p className="text-[12.5px] text-coral">{(m.error as Error).message}</p> : null}
      <button type="submit" disabled={m.isPending} className="self-start px-4 py-2 rounded-lg text-[13px] font-semibold bg-ink text-white disabled:opacity-55">
        {existing ? "Save" : "Connect this website"}
      </button>
    </form>
  );
}

/**
 * Everything about the connection, inside the tab rather than instead of it: four cards, in the order somebody
 * needs them — where the articles go, who they are written for, what they may be written from, and the two
 * technical things their developer needs once and never again.
 */
export function SiteSettings({ seo, onDone, onBusiness }: {
  seo: SeoResponse; onDone: () => void; onBusiness?: (() => void) | undefined;
}) {
  const site = seo.site!;
  const [key, setKey] = useState<string | null>(null);
  const [hook, setHook] = useState("");
  const makeKey = useMutation({ mutationFn: () => api.makeFeedKey(), onSuccess: (r) => { setKey(r.key); onDone(); } });
  const saveHook = useMutation({ mutationFn: () => api.setBuildHook(hook), onSuccess: () => { setHook(""); onDone(); } });

  return (
    <div className="max-w-[760px] flex flex-col gap-3">
      <Card title="Where the articles go" note={site.baseUrl.replace(/^https?:\/\//, "")}>
        <div className="flex flex-col gap-3.5">
          <Address address={site.baseUrl} onBusiness={onBusiness} />
          <Form existing={site} onDone={onDone} />
        </div>
      </Card>

      <SearchSettingsPanel seo={seo} onDone={onDone} />

      {site.kind === "website" ? (
        <Card title="Publishing" note="nothing to set up">
          <p className="text-[12.5px] text-muted leading-relaxed max-w-[62ch]">
            This is the website Joogoos built, so a published article goes on it by itself, with a Blog link on the home
            page. Pressing Publish on an article sends your site to Cloudflare again; taking one down does the same without it.
          </p>
        </Card>
      ) : (
        <>
      <Card title="For whoever builds your site" note="once, and never again">
        <p className="text-[12.5px] text-muted leading-relaxed max-w-[62ch]">
          Your published articles are served as JSON. Your site fetches this when it builds. Nothing here can
          write to your site, and Joogoos holds no key to it.
        </p>
        <pre className="mt-2 bg-page border border-line rounded-lg px-3 py-2 text-[12px] overflow-x-auto"><code>{site.feedUrl}</code></pre>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <button onClick={() => makeKey.mutate()} disabled={makeKey.isPending} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white">
            {site.feedKeyHint ? "Make a new read key" : "Make a read key"}
          </button>
          {site.feedKeyHint && !key ? <span className="text-[12px] text-muted tabular-nums">A key ending …{site.feedKeyHint} is in use.</span> : null}
        </div>
        {key ? (
          <div className="mt-2 rounded-lg border border-good/35 bg-good/8 px-3 py-2">
            <p className="text-[12px] font-semibold">Copy this now — it is not shown again.</p>
            <code className="block mt-1 text-[12px] break-all">{key}</code>
          </div>
        ) : null}
        <ol className="mt-3 flex flex-col gap-1.5 text-[12.5px] text-ink-2 leading-relaxed list-decimal pl-4 marker:text-faint marker:font-semibold">
          <li>In Cloudflare Pages: <b>Settings → Builds → Deploy hooks → Add</b>, branch <code>main</code>. Copy the address here.</li>
          <li>In <b>Settings → Variables and secrets</b>, add <code>JOOGOOS_FEED_URL</code> and, as a secret, <code>JOOGOOS_FEED_KEY</code>.</li>
          <li>At build time, fetch the feed with the key in an <code>Authorization: Bearer</code> header, and render each entry at its <code>path</code>.</li>
        </ol>
      </Card>

      <Card
        title="Build hook"
        note={site.buildHookSet ? "set" : "not set"}
      >
        <p className="text-[12.5px] text-muted leading-relaxed max-w-[62ch]">
          What Joogoos calls when you publish, so your site rebuilds. Stored encrypted and never shown again.
          {site.lastBuildAt ? ` Last asked ${new Date(site.lastBuildAt).toLocaleString()} — answered ${site.lastBuildStatus ?? "nothing"}.` : ""}
        </p>
        <div className="flex gap-2 mt-2">
          <input value={hook} onChange={(e) => setHook(e.target.value)} type="password" aria-label="Build hook"
            placeholder={site.buildHookSet ? "A build hook is set — paste a new one to replace it" : "https://api.cloudflare.com/client/v4/pages/webhooks/…"}
            className="flex-1 bg-page border border-line rounded-lg px-3 py-2 text-[13px]" />
          <button onClick={() => saveHook.mutate()} disabled={!hook || saveHook.isPending} className="px-3 py-2 rounded-lg text-xs font-semibold bg-ink text-white disabled:opacity-40">Save</button>
        </div>
        {saveHook.error ? <p className="text-[12.5px] text-coral mt-1">{(saveHook.error as Error).message}</p> : null}
      </Card>
        </>
      )}

      <DisconnectSite seo={seo} onDone={onDone} />
    </div>
  );
}

/**
 * The website disconnected (21 §7). Last on the page and asked once, because it is the business's site: what happens
 * to what is already out is said before the press, since the answer is different for a feed and for WordPress.
 */
function DisconnectSite({ seo, onDone }: { seo: SeoResponse; onDone: () => void }) {
  const [asking, setAsking] = useState(false);
  const m = useMutation({ mutationFn: () => api.disconnectSite(), onSuccess: () => { setAsking(false); onDone(); } });
  const site = seo.site!;
  return (
    <Card title="Disconnect this website">
      <p className="text-[12.5px] text-muted leading-relaxed max-w-[62ch]">
        Joogoos stops publishing to {site.baseUrl.replace(/^https?:\/\//, "")}. Your articles and drafts stay here, and
        connecting again picks them back up. The read key, the build hook and Search Console go.
        {site.kind === "website"
          ? " Articles already on your site stay until the next time your website is published."
          : site.kind === "wordpress"
          ? " Posts already on WordPress stay there, and Joogoos can no longer change or take them down."
          : " The feed stops answering, so your site's next build gets no articles from Joogoos."}
      </p>
      {asking ? (
        <div className="flex flex-wrap items-center gap-2 mt-2 text-[12.5px]">
          <span className="text-ink">Disconnect {site.baseUrl.replace(/^https?:\/\//, "")}?</span>
          <button onClick={() => m.mutate()} disabled={m.isPending} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-coral text-white disabled:opacity-55">Disconnect</button>
          <button onClick={() => setAsking(false)} className="text-muted hover:text-ink">Keep it</button>
        </div>
      ) : (
        <button onClick={() => setAsking(true)} className="mt-2 px-3 py-1.5 rounded-lg text-xs font-semibold border border-line-2 text-ink hover:border-coral hover:text-coral">
          Disconnect website
        </button>
      )}
      {m.error ? <p className="text-[12.5px] text-coral mt-1">{(m.error as Error).message}</p> : null}
    </Card>
  );
}

/**
 * Who the articles are for and what they may never say — on SEO → Your website, and as a step of setting up on
 * Business (ADR 0049 amendment), the same rows either way.
 */
export function WriterSettings({ seo, onDone }: { seo: SeoResponse; onDone: () => void }) {
  // Who buys it, from the Business page, while nobody has narrowed it (R-1): offered in the field, kept on Save.
  const offered = !seo.search.audience && !!seo.suggested.audience;
  const [audience, setAudience] = useState(seo.search.audience || seo.suggested.audience);
  const [neverName, setNeverName] = useState(seo.search.neverName.join("\n"));
  const [neverTopic, setNeverTopic] = useState(seo.search.neverTopic.join("\n"));
  const [neverClaim, setNeverClaim] = useState(seo.search.neverClaim.join("\n"));
  const lines = (s: string) => s.split("\n").map((l) => l.trim()).filter((l) => l.length > 1);
  const save = useMutation({
    mutationFn: () => api.saveSearchSettings({ audience, neverName: lines(neverName), neverTopic: lines(neverTopic), neverClaim: lines(neverClaim) }),
    onSuccess: onDone,
  });
  return (
    <Card title="What the writer knows" note="who it is for, and what it may never say">
      <div className="flex flex-col gap-3">
        <Field label="Who we write for" hint={offered
          ? "From Who buys it on your Business page. Narrow it if the articles are for some of them, then Save."
          : "May be narrower than who buys from you."}>
          <input value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full bg-page border border-line rounded-lg px-3 py-2 text-[13px]" />
        </Field>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Never name" hint={seo.suggested.rivals.length
            ? `Enforced by code. One a line. Also kept out, from your lookup: ${seo.suggested.rivals.join(", ")}.`
            : "Enforced by code. One a line. Your lookup found no other businesses, so only the names written here are kept out."}>
            <textarea value={neverName} onChange={(e) => setNeverName(e.target.value)} rows={4} className="w-full bg-page border border-line rounded-lg px-2 py-1.5 text-[12.5px]" />
          </Field>
          <Field label="Never write about" hint="Given to the writer. Checked by you.">
            <textarea value={neverTopic} onChange={(e) => setNeverTopic(e.target.value)} rows={4} className="w-full bg-page border border-line rounded-lg px-2 py-1.5 text-[12.5px]" />
          </Field>
          <Field label="Never claim" hint="Given to the writer. Checked by you.">
            <textarea value={neverClaim} onChange={(e) => setNeverClaim(e.target.value)} rows={4} className="w-full bg-page border border-line rounded-lg px-2 py-1.5 text-[12.5px]" />
          </Field>
        </div>
        <button onClick={() => save.mutate()} disabled={save.isPending} className="self-start px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white">
          {save.isPending ? "Saving…" : save.isSuccess ? "Saved" : "Save"}
        </button>
        {save.error ? <p className="text-bad text-xs">{save.error.message}</p> : null}
      </div>
    </Card>
  );
}

/**
 * The never-do lists, and the sources. `neverName` is the one code enforces; the other two are given to the writer
 * and checked by the person approving — and the screen says which is which, because a prompt is not a guarantee.
 */
function SearchSettingsPanel({ seo, onDone }: { seo: SeoResponse; onDone: () => void }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const add = useMutation({ mutationFn: () => api.addSource({ kind: "note", title, text, url: "" }), onSuccess: () => { setTitle(""); setText(""); onDone(); } });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteSource(id), onSuccess: onDone });

  return (
    <>
    <WriterSettings seo={seo} onDone={onDone} />

    <Card title="Your sources" note={`${seo.sources.length} ${seo.sources.length === 1 ? "source" : "sources"}`}>
      <p className="text-[12.5px] text-muted leading-relaxed max-w-[62ch]">
        The facts only you can speak to. Every factual sentence in an article cites one of these or your business
        page, and one that cites nothing is flagged before you can approve it.
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {seo.sources.map((s) => (
          <li key={s.id} className="flex items-start gap-2 bg-page border border-line rounded-lg px-3 py-2">
            <span className="px-1.5 py-0.5 rounded bg-good/12 text-[10px] font-bold tabular-nums text-good shrink-0">{s.ref}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-medium">{s.title}</span>
              <span className="block text-[12px] text-muted truncate">{s.preview}</span>
            </span>
            <button onClick={() => remove.mutate(s.id)} aria-label={`Remove ${s.title}`} className="text-muted hover:text-coral shrink-0"><Icon name="trash" size={14} /></button>
          </li>
        ))}
        {seo.sources.length === 0 ? <li className="text-[12.5px] text-faint">No sources yet — your business page is the only one an article can cite.</li> : null}
      </ul>
      <div className="mt-3 pt-3 border-t border-line flex flex-col gap-1.5">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What this source is" aria-label="Source title"
          className="w-full bg-page border border-line rounded-lg px-3 py-2 text-[13px]" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Paste the facts" aria-label="Source text"
          className="w-full bg-page border border-line rounded-lg px-3 py-2 text-[12.5px]" />
        <button onClick={() => add.mutate()} disabled={!title || !text || add.isPending} className="self-start px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white disabled:opacity-40">
          Add source
        </button>
      </div>
    </Card>
    </>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{label}</span>
      {children}
      {hint ? <span className="text-[11.5px] text-faint leading-snug">{hint}</span> : null}
    </label>
  );
}
